import { ChatConversationMessage, ChatMessageID } from '../models/chat';
import { ChatMeetingService } from './chat-meeting.service';
import { ChatMessageInputStore } from './chat-message-input.store';
import { ChatMessageReceiverService } from './chat-message-receiver.service';
import { ConversationsStore } from './conversations.store';
import { EmojiService } from './emoji.service';
import { MeetingsStore } from './meetings.store';
import { AuthServiceInterface } from './AuthServiceInterface';

export type UserProfile = { id: number; name: string; picture: { source: string } };

export class ChatMessageSenderService {
    static $inject = [
        'ChatMessageInputStore',
        'EmojiService',
        'ConversationsStore',
        'MeetingsStore',
        'ChatMeetingService',
        '$http',
        'AuthService',
        'GuidFactory',
        'ChatMessageReceiverService',
    ];

    get isProcessingUploads() {
        return this._isProcessingUploads$;
    }

    protected _isProcessingUploads$: Rx.BehaviorSubject<boolean> = new Rx.BehaviorSubject<boolean>(false);

    protected profile$: Rx.Observable<UserProfile>;

    constructor(
        private messageInputStore: ChatMessageInputStore,
        private emojiService: EmojiService,
        private conversationsStore: ConversationsStore,
        private meetingsStore: MeetingsStore,
        private chatMeetingService: ChatMeetingService,
        private $http: angular.IHttpService,
        private authService: AuthServiceInterface,
        private guidFactory: any,
        private chatMessageReceiverService: ChatMessageReceiverService
    ) {
        this.profile$ = Rx.Observable.of(authService.getUser()) as Rx.Observable<UserProfile>;

        this.chatMessageReceiverService.onNewMessage$.subscribe(({ meetingId, message }) =>
            this.onNewMessage(meetingId, message)
        );
        this.chatMessageReceiverService.onMessageUpdated$.subscribe(({ meetingId, message }) =>
            this.onMessageUpdated(meetingId, message)
        );
    }

    protected onNewMessage(meetingId: number, message: ChatConversationMessage) {
        this.conversationsStore.dispatchMessageCreateUpdate(meetingId, message);
        this.meetingsStore.dispatchNewMessage(meetingId, message);
    }

    protected onMessageUpdated(meetingId: number, message: ChatConversationMessage) {
        this.conversationsStore.dispatchMessageUpdated(meetingId, message);
        this.meetingsStore.dispatchMessageUpdated(meetingId, message);
    }

    public resend(message: ChatConversationMessage) {
        if (!message?.meeting_id) {
            return;
        }

        this.conversationsStore.dispatchMessageRemove(message.meeting_id, message);
        const { content, quoted_message, attachments, guid } = message;
        this.publishMessage(message.meeting_id, {
            content,
            quoted_message,
            attachments,
            guid,
        });
    }

    public cancel(message: ChatConversationMessage) {
        if (!message?.meeting_id) {
            return;
        }
        this.conversationsStore.dispatchMessageRemove(message.meeting_id, message);
    }

    public publish(conversationId: number) {
        this.messageInputStore
            .getMessageInputState$(conversationId)
            .take(1)
            .subscribe(async ({ content, quoted_message, uploads, editedMessage }) => {
                if (content.trim().length === 0 && uploads.length === 0) {
                    console.info('Cannot send empty message');
                    return;
                }

                if (editedMessage) {
                    return this.publishMessageUpdate(
                        editedMessage.id,
                        this.emojiService.convert(content),
                        quoted_message
                    );
                }
                const processUploads = async () => {
                    this.isProcessingUploads.onNext(true);

                    const processedUploads = await Promise.all(
                        uploads.map((u) => (u.remote_id ? Promise.resolve(u) : u.promise))
                    );

                    this.isProcessingUploads.onNext(false);

                    return processedUploads;
                };

                const attachments = await processUploads();

                this.publishMessage(conversationId, {
                    content: this.emojiService.convert(content),
                    quoted_message,
                    attachments: attachments.map((a) => ({ hash: a?.remote_id })),
                });
            });
    }

    private publishMessageUpdate(messageId: ChatMessageID, message: string, quotedMessage?: ChatConversationMessage) {
        const updateMessage = () =>
            this.$http.put(
                `/api/v2.0/chat/messages/${messageId}`,
                {
                    content: message,
                    ...(quotedMessage && { quoted_message: quotedMessage?.id }),
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
        const pendingMessage = updateMessage();
    }

    private publishMessage(
        conversationId: number,
        { content, quoted_message, attachments, guid }: { content: string } & Partial<ChatConversationMessage>
    ) {
        this.profile$.subscribe((user) => {
            const makeNewGuid = () => 'C-' + this.guidFactory.Generate();
            const messageGuid = guid ?? makeNewGuid();

            const createMessage = () =>
                this.$http.post(
                    '/api/v2.0/chat/messages',
                    {
                        meeting: conversationId,
                        content: content,
                        attachments,
                        ...(quoted_message && { quoted_message: quoted_message?.id }),
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Ph-Guid': messageGuid,
                            'Idempotency-Key': messageGuid,
                        },
                    }
                );

            const pendingMessage = createMessage();

            const mapPictureUrl = (user: UserProfile) => ({
                ...user,
                picture_url: user?.picture?.source,
            });

            const optimisticMessage: ChatConversationMessage = {
                // FIXME: without setting `id` the optimistic sending fails
                id: messageGuid,
                guid: messageGuid,
                meeting_id: conversationId,
                type: 'message',
                attachments: [],
                content,
                owner: mapPictureUrl(user),
                quoted_message,
                created_at: new Date(),
                place: {
                    id: conversationId,
                },
            };

            this.conversationsStore.dispatchMessageCreateUpdate(conversationId, optimisticMessage as any);

            pendingMessage.catch((e) => {
                if (409 === (e as ng.IHttpResponse<unknown>).status) {
                    // idempotency key error - the server has already seen this message,
                    // let's just pretend nothing happened and the message should appear on the message list,
                    // also after a reload
                    console.warn('Message rejected - idempotency key error');
                    return;
                }

                this.conversationsStore.dispatchMessageCreateUpdate(conversationId, {
                    ...optimisticMessage,
                    failed: true,
                } as any);
            });
        });
    }

    private log(...args: any[]) {
        console.log.apply(console, ['ChatMessageSenderService', ...args]);
    }
}

angular.module('tradersyard').service('ChatMessageSenderService', ChatMessageSenderService);
